import { useEffect, useState } from 'react';
import { api, type ProjectListItem, type SyncAppFile } from '../api';
import { ButtonAnchor, Card, Page } from '../ui';

function humanSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Página de download do app de sincronização de docs + passo-a-passo de conexão.
 * Os instaladores são servidos pelo próprio bug-pilot (/downloads); a lista vem
 * de /v1/sync-app/releases. O app em si é um projeto separado (bug-pilot-docs-sync).
 */
export function SyncAppDownload() {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [files, setFiles] = useState<SyncAppFile[] | null>(null);
  const apiBaseUrl = window.location.origin;

  useEffect(() => {
    api.listProjects().then(({ projects }) => setProjects(projects)).catch(() => {});
    api.listSyncAppReleases().then(({ files }) => setFiles(files)).catch(() => setFiles([]));
  }, []);

  return (
    <Page
      title="App de sincronização de Docs"
      subtitle="Mantenha uma pasta local sincronizada com os documentos de um projeto."
    >
      <div className="settings" style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
        <Card>
          <h2 style={{ marginTop: 0 }}>1. Baixe o app</h2>
          <p style={{ color: 'var(--color-muted)' }}>
            Roda minimizado na bandeja, puxa os docs continuamente e salva suas edições
            automaticamente (intervalo configurável), com botão e atalho global pra
            sincronizar na hora.
          </p>
          {files === null ? (
            <p style={{ color: 'var(--color-muted)' }}>Carregando…</p>
          ) : files.length === 0 ? (
            <p style={{ color: 'var(--color-muted)' }}>
              ⏳ O app ainda não foi publicado aqui — o instalador aparece nesta página
              assim que o build (Windows <code>.msi/.exe</code> e macOS <code>.dmg</code>)
              for enviado pro servidor. Volte em breve.
            </p>
          ) : (
            <div className="inline-form" style={{ gap: 8, flexWrap: 'wrap' }}>
              {files.map((f) => (
                <ButtonAnchor key={f.name} href={f.url} variant="primary" size="sm">
                  {f.platform}{' '}
                  <span style={{ opacity: 0.7 }}>({humanSize(f.size)})</span>
                </ButtonAnchor>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h2 style={{ marginTop: 0 }}>2. Abra o app e conecte</h2>
          <p style={{ color: 'var(--color-muted)' }}>
            Na primeira abertura, confirme o servidor (<code>{apiBaseUrl}</code>) e clique
            em <strong>Conectar</strong>. O app abre este site no navegador com um código —
            você confirma aqui (logado) e pronto, sem colar senha nem token.
          </p>
          <p style={{ color: 'var(--color-muted)', marginBottom: 0, fontSize: 13 }}>
            A confirmação acontece em <code>{apiBaseUrl}/connect</code>.
          </p>
        </Card>

        <Card>
          <h2 style={{ marginTop: 0 }}>3. Escolha o projeto e a pasta</h2>
          <p style={{ color: 'var(--color-muted)' }}>
            {projects.length === 1 ? (
              <>
                O projeto <code>{projects[0]!.slug}</code> é selecionado automaticamente.
              </>
            ) : (
              'Selecione o projeto a sincronizar'
            )}{' '}
            e a <strong>pasta local</strong> onde os docs vão ficar.
          </p>
          <p style={{ color: 'var(--color-muted)', marginBottom: 0 }}>
            Pronto. O app fica na bandeja: o popup tem o botão “Sincronizar agora”, e o
            auto-save sobe suas mudanças a cada X segundos. <em>Puxar</em> é contínuo e
            sempre atualiza a pasta com o servidor.
          </p>
        </Card>
      </div>
    </Page>
  );
}
